import React from 'react';

// Styles & Assets
import styles from './DialogVitalSigns.module.css';

// Services
import { getUser } from '../../../services/Auth';
import { reverseParseDate } from '../../../services/parseDates';
import { equalNumsWithNaN } from '../../../services/General';
import { getTimestampFirstParam } from '../../../services/Patient';
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useState } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// Components
import VitalSignInput from '../VitalSignInput/VitalSignInput';

// Interfaces
import { DataElementByHour, PhysiologicalMonitoring } from '../../../interfaces/PhysiologicalMonitoring';
import { Parameter } from '../../../interfaces/Profile';
import { PatientOutletContext } from '../../../interfaces/Patient';
import { ElementsDispatch } from '../../../interfaces/Reducers';

// Language
import { useTranslation } from 'react-i18next';

// Other
import { useOutletContext } from 'react-router-dom';

const DialogVitalSigns: React.FC<{
    parameters: Parameter[],
    dataElementHour: DataElementByHour
    showModal: React.Dispatch<React.SetStateAction<boolean>>,
    dispatchElements: React.Dispatch<ElementsDispatch>
}> = ({ parameters, dataElementHour, showModal, dispatchElements }) => {

    const { t } = useTranslation();
    const { patient, caso }: PatientOutletContext = useOutletContext();

    const toast = useToast();

    const initDictionary = () => {
        return Object.assign({}, ...parameters.map((param => {
            const dataElement = getTimestampFirstParam(param.id || '', dataElementHour);
            const valueNum = dataElement?.values[param.id] === null ?
                NaN : Number(dataElement?.values[param.id]);
            return {
                [param.id]: {
                    value: valueNum,
                    oldValue: valueNum,
                    comment: dataElement?.comments[param.id + '_comment'] || '',
                    oldComment: dataElement?.comments[param.id + '_comment'] || '',
                }
            };
        })));
    };

    const [paramValues, setParamValues] = useState<{
        [name: string]: { value: number, oldValue: number, comment: string, oldComment: string }
    }>(initDictionary());

    const { sendRequest } = useHttp<PhysiologicalMonitoring>();

    const handleConfirm = (e: React.FormEvent) => {

        e.preventDefault();

        const user = getUser();

        Object.keys(paramValues).forEach(paramKey => {
            const param = paramValues[paramKey];
            const dataElement = getTimestampFirstParam(paramKey, dataElementHour);

            if (!equalNumsWithNaN(param.oldValue, param.value) || param.comment !== param.oldComment) {
                const comment = param.comment !== param.oldComment ? { comment: param.comment } : {};
                const payload = {
                    case_id: caso.id,
                    deleted: false,
                    modified: false,
                    patient_id: patient.id,
                    sheet_parameter_id: paramKey,
                    time: reverseParseDate(dataElement?.date),
                    user_id: user.id,
                    value: isNaN(param.value) ? null : param.value,
                    ...comment,
                };
                const postState = (data: PhysiologicalMonitoring) => {
                    dispatchElements({ type: 'ADD_PHYSIOLOGICAL', date: dataElement.date, key: paramKey, physiological: data, comment: comment.comment });
                };
                const putState = () => {
                    dispatchElements({ type: 'UPDATE_PHYSIOLOGICAL', date: dataElement.date, key: paramKey, value: param.value, comment: comment.comment, userID: user.id });
                };

                if (dataElement?.values[paramKey + '_db_id'] === undefined) {
                    sendRequest({
                        url: getEnvironmentApiUrl() + 'physiological_monitoring',
                        method: 'POST',
                        body: payload
                    }, postState, () => {
                        const param = parameters.find(parameter => parameter.id === paramKey);
                        toast.error(`${t('error_adding')} ${param?.name}`);
                    });
                }
                else if (dataElement?.values[paramKey + '_db_id'] !== null) {
                    sendRequest({
                        url: getEnvironmentApiUrl() + 'physiological_monitoring/' + dataElement?.values[paramKey + '_db_id'],
                        method: 'PUT',
                        body: {
                            ...payload,
                            modified: true,
                        }
                    }, putState, () => {
                        const param = parameters.find(parameter => parameter.id === paramKey);
                        toast.error(`${t('error_adding')} ${param?.name}`);
                    });
                }
            }
        });
        showModal(false);
    };

    const handleChange = (paramID: string, value?: number, comment?: string) => {
        setParamValues(oldParams => {
            const copyOldParams = { ...oldParams };
            if (value !== undefined) copyOldParams[paramID].value = value;
            if (comment !== undefined) copyOldParams[paramID].comment = comment;
            return copyOldParams;
        });
    };

    return (
        <form className='dialog' onSubmit={handleConfirm}>
            <h1 className='dialogTitle'>{t('enter_vital_signs')}</h1>
            <h2 className='dialogSubTitle'>{dataElementHour?.date}</h2>
            <div className={styles.form}>
                {parameters.map(param => {
                    return (
                        <VitalSignInput
                            key={param.id}
                            param={param}
                            defaultNum={getTimestampFirstParam(param.id || '', dataElementHour).values[param.id]}
                            defaultComment={getTimestampFirstParam(param.id || '', dataElementHour).comments[param.id + '_comment']}
                            handleChange={handleChange}
                        />
                    );
                })}
            </div>
            <div className={styles.buttonsContainer}>
                <button type='button' onClick={() => showModal(false)} className={`button ${styles.cancel}`}>
                    {t('cancel')}
                </button>
                <button type='submit' className={`button ${styles.confirm}`}>
                    {t('confirm')}
                </button>
            </div>
        </form>
    );
};

export default DialogVitalSigns;